import Plus from "assets/img/svg/plus";
import CopyToClipboard from "components/shared/CopyToClipboard";
import DeleteModal from "components/shared/DeleteModal";
import ModalBase from "components/shared/ModalBase";
import PenEditButton from "components/shared/PenEditButton";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import TitleSeparation from "components/shared/TitleSeparation";
import { BodyHighlight, SmallBody } from "helpers/TypographyComponents";
import { useAbility } from "hooks/useAbility";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IAdvanceCustomStatus } from "store/advanceCustomStatusStore";
import { deleteAdvanceCustomStatus, fetchAdvanceCustomStatuses } from "store/advanceCustomStatusStore";
import { useAppDispatch, useAppSelector } from "store/store";

import AdvanceCustomStatusForm from "./AdvanceCustomStatusForm";
import styles from "./style.module.scss";

const ADVANCE_STATUS_STEPS = [
    { titleKey: "AdvanceStatusesStepCreateTitle", descriptionKey: "AdvanceStatusesStepCreateDescription" },
    { titleKey: "AdvanceStatusesStepAssignTitle", descriptionKey: "AdvanceStatusesStepAssignDescription" },
] as const;

const AdvanceCustomStatusSettings = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const ability = useAbility();
    const { items, status } = useAppSelector((state) => state.advanceCustomStatuses);
    const [modalType, setModalType] = useState<"create" | "edit" | "delete" | "">("");
    const [chosenStatus, setChosenStatus] = useState<IAdvanceCustomStatus | undefined>(undefined);
    const canCreate = ability.can("create", "AdvanceCustomStatus");
    const canUpdate = ability.can("update", "AdvanceCustomStatus");

    useEffect(() => {
        if (status === "idle") {
            dispatch(fetchAdvanceCustomStatuses());
        }
    }, [dispatch, status]);

    const closeModal = () => {
        setModalType("");
        setChosenStatus(undefined);
    };

    const handleDelete = async () => {
        if (!chosenStatus) return;
        const response = await dispatch(deleteAdvanceCustomStatus({ id: chosenStatus.id }));
        if (response.meta.requestStatus === "fulfilled") {
            closeModal();
        }
    };

    return (
        <div className={styles["advance-status-settings"]}>
            <div className={styles.header}>
                <h1>{t("Settings.AdvanceStatuses")}</h1>
                {canCreate && (
                    <StyledButton
                        buttonType="primary"
                        onClick={() => {
                            setChosenStatus(undefined);
                            setModalType("create");
                        }}
                    >
                        <Plus />
                        {t("Settings.AddAdvanceStatus")}
                    </StyledButton>
                )}
            </div>

            <div className={styles.guide}>
                <TitleSeparation
                    title={t("Settings.AdvanceStatusesHowItWorks")}
                    description={t("Settings.AdvanceStatusesHowItWorksDescription")}
                    style={{ marginTop: "1.5rem" }}
                />
                <ol className={styles.steps}>
                    {ADVANCE_STATUS_STEPS.map(({ titleKey, descriptionKey }, index) => (
                        <li key={titleKey} className={styles.step}>
                            <span className={styles["step-number"]}>{index + 1}</span>
                            <div className={styles["step-content"]}>
                                <BodyHighlight>{t(`Settings.${titleKey}`)}</BodyHighlight>
                                <SmallBody color="grey">{t(`Settings.${descriptionKey}`)}</SmallBody>
                            </div>
                        </li>
                    ))}
                </ol>
            </div>

            <div className={styles["list-section"]}>
                <TitleSeparation
                    title={t("Settings.AdvanceStatusesListSection")}
                    description={t("Settings.AdvanceStatusesListSectionDescription")}
                    style={{ marginTop: "2.5rem" }}
                />

                {status === "pending" && (
                    <div className={styles["spinner-container"]}>
                        <Spinner />
                    </div>
                )}

                {status !== "pending" && items.length === 0 && (
                    <div className={styles["empty-state"]}>
                        <p>{t("Settings.AdvanceStatusesEmpty")}</p>
                        <span>{t("Settings.AdvanceStatusesEmptyHint")}</span>
                    </div>
                )}

                {status !== "pending" && items.length > 0 && (
                    <div className={styles["advance-status-list"]}>
                        {items.map((advanceStatus) => (
                            <div key={advanceStatus.id} className={styles["advance-status-row"]}>
                                <div className={styles["advance-status-identity"]}>
                                    <span className={styles["advance-status-name"]}>{advanceStatus.name}</span>
                                    {advanceStatus.customIdentifier && (
                                        <CopyToClipboard
                                            className={styles["advance-status-id"]}
                                            color="var(--foreground)"
                                        >
                                            {advanceStatus.customIdentifier}
                                        </CopyToClipboard>
                                    )}
                                </div>
                                {canUpdate && (
                                    <div className={styles["advance-status-actions"]}>
                                        <PenEditButton
                                            tooltipText={t("Settings.AdvanceStatusEdit")}
                                            onClick={() => {
                                                setChosenStatus(advanceStatus);
                                                setModalType("edit");
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <ModalBase
                open={modalType === "create" || modalType === "edit"}
                onClose={closeModal}
                modalTitle={modalType === "edit" ? t("Settings.EditAdvanceStatus") : t("Settings.NewAdvanceStatus")}
                modalSubtitle={t("Settings.AdvanceStatusFormHint")}
            >
                <AdvanceCustomStatusForm
                    key={chosenStatus?.id ?? "new"}
                    advanceStatus={chosenStatus}
                    onClose={closeModal}
                    onRequestDelete={
                        ability.can("delete", "AdvanceCustomStatus") ? () => setModalType("delete") : undefined
                    }
                />
            </ModalBase>

            <DeleteModal
                open={modalType === "delete"}
                onClose={closeModal}
                onDelete={handleDelete}
                elementTitle={chosenStatus?.name ?? ""}
                needsDoubleQuotes
                description={t("Settings.DeleteAdvanceStatusDescription")}
            />
        </div>
    );
};

export default AdvanceCustomStatusSettings;
